<template>
  <div
    id="nav"
    v-click-outside="close"
    class="navigation-list"
    :class="[isToggled ? 'on' : 'off']"
  >
    <a
      id="navigation-toggle"
      ref="toggleButton"
      href="#"
      :class="'toggle' + (isToggled ? ' on' : ' off')"
      @click="toggle"
    >
      <icon
        v-if="isToggled"
        key="isOpen"
        name="fas fa-times"
      />
      <icon
        v-else
        key="isClosed"
        name="fas fa-bars"
      />
    </a>
    <div
      v-if="isToggled"
    >
      <div
        v-if="isLoggedIn"
        key="logged-in"
        class="flex flex-col"
      >
        <router-link
          id="nav-dashboard"
          :to="{name: 'dashboard'}"
          class="router-link"
        >
          <icon
            name="fas fa-house-user"
            container-class="navigation-icon-container"
          />Dashboard
        </router-link>
        <a
          id="nav-auctions"
          href="/auctions"
          class="router-link"
        >
          <icon
            name="fas fa-book"
            container-class="navigation-icon-container"
          />Auctions
        </a>
        <collapse-expand
          slot-wrapper-class="flex flex-col px-2 ml-4 navigation-subnav"
          :condition="true"
        >
          <router-link
            id="nav-auctions-active"
            :to="{name: 'auctions-active'}"
            class="router-link"
          >
            <icon
              name="fas fa-hammer"
              container-class="navigation-icon-container theme-state-live"
            />Active
          </router-link>
          <router-link
            id="nav-auctions-hot"
            :to="{name: 'auctions-hot'}"
            class="router-link"
          >
            <icon
              name="fas fa-fire"
              container-class="navigation-icon-container theme-state-hot"
            />Hot
          </router-link>
          <router-link
            id="nav-auctions-soon"
            :to="{name: 'auctions-soon'}"
            class="router-link"
          >
            <icon
              name="fas fa-hammer"
              container-class="navigation-icon-container theme-state-soon"
            />Starting Soon
          </router-link>
          <router-link
            id="nav-auctions-closed"
            :to="{name: 'auctions-closed'}"
            class="router-link"
          >
            <icon
              name="fas fa-hammer"
              container-class="navigation-icon-container theme-state-closed"
            />Closed
          </router-link>
        </collapse-expand>
        <a
          v-if="canManage"
          id="nav-manage"
          href="#"
          class="router-link"
          @click.prevent="changeManage(manage)"
        >
          <icon
            name="fas fa-book"
            container-class="navigation-icon-container"
          />Manage
        </a>
        <collapse-expand
          slot-wrapper-class="flex flex-col px-2 ml-4 navigation-subnav"
          :condition="manage"
        >
          <router-link
            v-if="can('manage_auctions')"
            id="nav-manage-winners"
            :to="{name: 'auction-winners'}"
            class="router-link"
          >
            <icon
              name="fas fa-wallet"
              container-class="navigation-icon-container"
            />Winners
          </router-link>
          <router-link
            v-if="can('manage_auctions')"
            id="nav-manage-auctions"
            :to="{name: 'manage-auctions'}"
            class="router-link"
          >
            <icon
              name="fas fa-hammer"
              container-class="navigation-icon-container"
            />Auctions
          </router-link>
          <router-link
            v-if="can('manage_auctions')"
            id="nav-import-auctions"
            :to="{name: 'import-auctions'}"
            class="router-link"
          >
            <icon
              name="fas fa-upload"
              container-class="navigation-icon-container"
            />Import
          </router-link>
          <router-link
            v-if="can('manage_auctions') && can('manage_users')"
            id="nav-manage-bids"
            :to="{name: 'manage-bids'}"
            class="router-link"
          >
            <icon
              name="fas fa-gavel"
              container-class="navigation-icon-container"
            />Bids
          </router-link>
          <router-link
            v-if="can('manage_auctions') && can('manage_users')"
            id="nav-delete-auctions"
            :to="{name: 'delete-auctions'}"
            class="router-link"
          >
            <icon
              name="fas fa-hammer"
              container-class="navigation-icon-container"
            />Delete Auctions
          </router-link>
          <router-link
            v-if="can('manage_users')"
            id="nav-manage-users"
            :to="{name: 'manage-users'}"
            class="router-link"
          >
            <icon
              name="fas fa-users"
              container-class="navigation-icon-container"
            />Users
          </router-link>
        </collapse-expand>
      </div>
      <a
        id="logout"
        href="#"
        class="router-link"
        @click.prevent="$emit('logout')"
      >
        <icon
          name="fas fa-sign-out-alt"
          container-class="navigation-icon-container"
        />Logout
      </a>
    </div>
  </div>
</template>

<script>
import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons/faAddressBook';
import { faArrowCircleUp } from '@fortawesome/free-solid-svg-icons/faArrowCircleUp';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';
import { faCaretLeft } from '@fortawesome/free-solid-svg-icons/faCaretLeft';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons/faCaretRight';
import { faClone } from '@fortawesome/free-solid-svg-icons/faClone';
import { faCogs } from '@fortawesome/free-solid-svg-icons/faCogs';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faExpandAlt } from '@fortawesome/free-solid-svg-icons/faExpandAlt';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons/faFileUpload';
import { faFilm } from '@fortawesome/free-solid-svg-icons/faFilm';
import { faFire } from '@fortawesome/free-solid-svg-icons/faFire';
import { faGavel } from '@fortawesome/free-solid-svg-icons/faGavel';
import { faHammer } from '@fortawesome/free-solid-svg-icons/faHammer';
import { faHeart } from '@fortawesome/free-solid-svg-icons/faHeart';
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome';
import { faHouseUser } from '@fortawesome/free-solid-svg-icons/faHouseUser';
import { faImages } from '@fortawesome/free-solid-svg-icons/faImages';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons/faLayerGroup';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons/faLightbulb';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faPoll } from '@fortawesome/free-solid-svg-icons/faPoll';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons/faSignOutAlt';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faSun } from '@fortawesome/free-solid-svg-icons/faSun';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faWallet } from '@fortawesome/free-solid-svg-icons/faWallet';
import { faWindowClose } from '@fortawesome/free-solid-svg-icons/faWindowClose';
import { faWrench } from '@fortawesome/free-solid-svg-icons/faWrench';
import vClickOutside from 'click-outside-vue3';
import Icon from './Icon.vue';
import CollapseExpand from '../transitions/collapse.vue';

library.add(
  faAddressBook,
  faArrowCircleUp,
  faBars,
  faBook,
  faCaretLeft,
  faCaretRight,
  faClone,
  faCogs,
  faComment,
  faExpandAlt,
  faFileUpload,
  faFilm,
  faFire,
  faGavel,
  faHammer,
  faHeart,
  faHome,
  faHouseUser,
  faImages,
  faLayerGroup,
  faLightbulb,
  faMinus,
  faMoon,
  faPlus,
  faPoll,
  faSignOutAlt,
  faSpinner,
  faSun,
  faThumbsUp,
  faTimes,
  faUpload,
  faUser,
  faUsers,
  faWallet,
  faWindowClose,
  faWrench,
);

dom.i2svg();
dom.watch();

export default {
  name: 'NavigationFile',
  components: {
    CollapseExpand,
    Icon,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    isLoggedIn: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    isToggled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:isToggled', 'logout'],
  data() {
    return {
      settings: false,
      manage: false,
    };
  },
  computed: {
    canManage() {
      return this.user.role.manage_users || this.user.role.manage_auctions;
    },
  },
  methods: {
    can(permission) {
      return this.user.role[permission];
    },
    changeManage(manage) {
      const vm = this;
      vm.manage = !manage;
    },
    close() {
      this.$emit('update:isToggled', false);
    },
    toggle() {
      this.$emit('update:isToggled', !this.isToggled);
    },
  },
};
</script>

<style scoped>
@reference "tailwindcss";

.navigation-list {
  @apply text-lg px-4 py-2 absolute z-50;
  background: transparent;
  color: var(--text);
  top: 0;
  left: 0;

  @variant md {
    @apply border-r pt-4 border-b-0 relative mt-20;
    background-color: var(--surface);
    border-color: var(--border);
    top: auto;
    left: auto;
  }

  @variant lg {
    @apply mt-24;
  }

  &.on {
    @apply flex-col justify-end border-r border-b border-solid;
    background-color: var(--surface);
    border-color: var(--border);

    @variant md {
      @apply w-1/2 flex-col justify-end border-b-0;
    }

    @variant lg {
      @apply w-1/5;
    }

    @variant xl {
      @apply w-1/6;
    }
  }

  &.off {
    @apply shrink;
  }

  a, button {
    @apply block w-full no-underline text-left rounded-md py-3 px-4 font-bold
    transition duration-150 ease-in-out;
    color: var(--text);

    &:visited {
      @apply no-underline;
      color: var(--text);
    }

    /**
     * Darken into the page rather than flashing a pale fill. The old
     * hover was bg-blue-100 with blue-800 type, a light chip on a dark
     * field.
     */
    &:hover, &:active {
      background-color: var(--surface-card);
      color: var(--text-strong);
    }

    /**
     * The page you are on. A rule down the left rather than a bar
     * underneath, so it reads as a position in a list.
     */
    &.router-link-active {
      @apply relative font-bold bg-transparent cursor-default;
      color: var(--text-accent);

      &::after {
        @apply absolute inset-y-0 left-0 w-1 rounded-r;
        background-color: var(--action);
        content: "";
      }

      &:hover, &:active, &:visited {
        @apply bg-transparent;
        color: var(--text-accent);
      }
    }

    &.toggle {
      @apply w-10 h-10 p-0 m-0 leading-10 text-center block;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--text-accent);
      outline-offset: -2px;
    }
  }
}

.router-link {
  white-space: nowrap;
}
</style>
